import React, { useState } from "react";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { GatsbyImage } from "gatsby-plugin-image";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import cn from "classnames";
import { BALI, JAKARTA_LOCATION } from "store/constants/hotel";
import * as REASON from "store/constants/support";
import { renderForMobile } from "utils/renderForMobile";
import Drawer from "components/Design/Drawer/Drawer";
import DrawerHead from "components/Design/Drawer/DrawerHead";
import ButtonNav from "components/Design/Button/NavButton";
import Icon from "components/CommonIcon/Icon";
import { useLocation } from "@reach/router";
import { fullWidth } from "components/Design/ArtDirectedImage";
import ReachUs from "components/ReachUs/ReachUs";
import { generateSupportURL } from "utils/generateSupportURL";
import { urlPath } from "utils/slugGenerator";

const SupportDrawer = ({ visible, close, content, supportReachUsLink }) => {
  const [accordion, setAccordion] = useState(false);
  const { t } = useTranslation("homepage");
  const breakpoints = useBreakpoint();
  const { pathname } = useLocation();

  const bannerImageSrc =
    Boolean(content.images) &&
    content.images?.map(({ mobile, desktop }) =>
      renderForMobile(breakpoints) ? mobile : desktop
    )[0];

  const ontoggle = () => {
    setAccordion(!accordion);
  };

  const drawerPlacement = renderForMobile(breakpoints) ? "bottom" : "right";

  const parentPath = () => {
    return content.hotel.location === BALI
      ? "bali"
      : urlPath[content.hotel?.id];
  };

  const reasonInquiry = () => {
    if (content.hotel?.id === "5") return REASON.CRUISE_INQUIRY;
    return REASON.GENERAL_INQUIRY;
  };

  const urlIsSupport = pathname.includes("/support");
  const urlIsFaq = pathname.includes("/faq");

  return (
    <Drawer visible={visible} placement={drawerPlacement}>
      <div className="absolute top-0 left-0 w-full z-1">
        <DrawerHead
          title={<div className="text-h5">{t(content.title)}</div>}
          onClose={close}
        />
      </div>
      <div className="h-full overflow-auto pt-60">
        <GatsbyImage
          image={fullWidth(bannerImageSrc)}
          className="w-full"
          alt="Image"
        />
        <div className="px-20 pb-20">
          <h2 className="font-semibold text-26-36 text-gray-text text-center mt-40 mb-16">
            {t(content.description)}
          </h2>
          <div className="font-mulish bg-solid-white pt-20 mb-16">
            <div className="text-center pb-24 px-16">
              {Boolean(content.hotel?.offices) &&
                content.hotel.offices.map(
                  (office) =>
                    office.name === content.hotel?.name && (
                      <>
                        <h5 className="font-bold text-gold text-12-22 tracking-16 uppercase mb-12">
                          {t(office.title)}
                        </h5>
                        <span className="inline-block font-extrabold text-12-22 text-gray-text-2 tracking-16 mb-12 border-b-2 border-gray-text-2">
                          {office.phone}
                        </span>
                        <p className="font-semibold text-gray-text-2 text-12-22 mb-12">
                          {office.email}
                        </p>
                      </>
                    )
                )}

              {(Boolean(content.hotels) ||
                Boolean(content.hotel.offices.length > 1)) && (
                <div
                  className="inline-flex items-center font-semibold text-gold text-12-28 tracking-8 uppercase cursor-pointer"
                  onClick={ontoggle}>
                  <span
                    className={cn("mr-6", {
                      "transform -rotate-90 -mt-6": accordion,
                    })}>
                    <Icon name="right-arrow" fontSize="text-20" />
                  </span>
                  {t(Boolean(accordion) ? "Less info" : "More info")}
                </div>
              )}
            </div>

            {(Boolean(content.hotels) || Boolean(content.hotel.offices)) &&
              Boolean(accordion) && (
                <div className="flex flex-col space-y-20 px-16 pt-14 pb-20 border-t border-gray-disable">
                  {content.hotel.offices.map(
                    (office) =>
                      office.name !== content.hotel?.name && (
                        <div key={office.name}>
                          <h5 className="font-bold text-gold text-12-22 tracking-16 uppercase mb-8">
                            {office.title}
                          </h5>
                          <div className="flex justify-between font-mulish">
                            <span className="font-semibold text-14-24 text-gray-text mb-8">
                              {t("Phone")}
                            </span>
                            <span className="text-14-24 text-gray-text-2">
                              {office.phone}
                            </span>
                          </div>
                          <div className="flex justify-between font-mulish">
                            <span className="font-semibold text-14-24 text-gray-text mb-8">
                              {t("Email")}
                            </span>
                            <span className="text-14-24 text-gray-text-2">
                              {office.email}
                            </span>
                          </div>
                        </div>
                      )
                  )}
                </div>
              )}
          </div>
          <div className="flex flex-col space-y-16">
            <ReachUs supportReachUsLink={supportReachUsLink} />
            <ButtonNav
              href={generateSupportURL(parentPath(), reasonInquiry())}
              prefixIcon={<Icon name="comment" />}
              label={t("Contact Us")}
              onClick={urlIsSupport ? close : null}
            />
            {content.hotel.location !== JAKARTA_LOCATION && (
              <ButtonNav
                href={`/${parentPath()}/faq`}
                prefixIcon={<Icon name="faq" />}
                label={t("FAQ")}
                onClick={urlIsFaq ? close : null}
              />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

SupportDrawer.defaultProps = {
  content: {},
};

export default SupportDrawer;
